import type { CrmChatProviderKey } from '@/api/types/crm/crmLeadFunnels.types'
import { parseCrmChatProviderKey } from '@pages/Admin/CRM/components/integrations/common/lib/crmChatProviderKey'

/** Полная навигация дока интеграционных чатов */
export const INTEGRATION_CHAT_DOCK_NAVIGATION_STORAGE_KEY =
  'impulse_integration_chat_dock_navigation'

/** @deprecated читается только для миграции */
const LEGACY_INTEGRATION_CHAT_DOCK_STORAGE_KEY = 'impulse_integration_chat_dock'

export type IntegrationChatDockScreen =
  | 'integrations-picker'
  /** @deprecated мигрируется в integrations-picker */
  | 'wazzup-accounts'
  | 'wazzup-channels'
  | 'chats-list'
  | 'conversation'

export type IntegrationChatDockPersistedState = {
  /** false — панель свёрнута (лаунчер), сессия сохранена */
  expanded: boolean
  screen: IntegrationChatDockScreen
  activeProvider: CrmChatProviderKey | null
  /** Вкладка «Все» / конкретный бот в списке чатов канала */
  chatsListIntegrationId: number | null
  /** Wazzup: выбранный канал (external_channel_id). */
  chatsListExternalChannelId: string | null
  leadId: number | null
  conversationId: number | null
}

export type IntegrationChatDockInitialSession =
  IntegrationChatDockPersistedState

const DEFAULT_PERSISTED_STATE: IntegrationChatDockPersistedState = {
  expanded: false,
  screen: 'integrations-picker',
  activeProvider: null,
  chatsListIntegrationId: null,
  chatsListExternalChannelId: null,
  leadId: null,
  conversationId: null,
}

const parsePositiveInt = (value: unknown): number | null => {
  if (value == null) {
    return null
  }
  const numeric = Number(value)
  if (!Number.isFinite(numeric) || numeric <= 0) {
    return null
  }
  return Math.floor(numeric)
}

const parseIntegrationChatDockScreen = (
  value: unknown,
): IntegrationChatDockScreen => {
  if (value === 'integrations-picker') {
    return 'integrations-picker'
  }
  // Legacy: экран выбора аккаунтов убран — аккаунты на integrations-picker.
  if (value === 'wazzup-accounts') {
    return 'integrations-picker'
  }
  if (value === 'wazzup-channels') {
    return 'wazzup-channels'
  }
  if (value === 'chats-list') {
    return 'chats-list'
  }
  if (value === 'conversation') {
    return 'conversation'
  }
  return 'integrations-picker'
}

const parseProvider = (value: unknown): CrmChatProviderKey | null =>
  typeof value === 'string' ? parseCrmChatProviderKey(value) : null

const parseExternalChannelId = (value: unknown): string | null => {
  if (typeof value !== 'string') {
    return null
  }
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : null
}

const sanitizePersistedState = (
  raw: {
    expanded?: boolean
    screen?: unknown
    activeProvider?: CrmChatProviderKey | null
    chatsListIntegrationId?: number | null
    chatsListExternalChannelId?: string | null
    leadId?: number | null
    conversationId?: number | null
  },
): IntegrationChatDockPersistedState => {
  const expanded = Boolean(raw.expanded)
  let screen = parseIntegrationChatDockScreen(raw.screen)
  const activeProvider = parseProvider(raw.activeProvider)
  const chatsListIntegrationId = parsePositiveInt(raw.chatsListIntegrationId)
  let chatsListExternalChannelId = parseExternalChannelId(
    raw.chatsListExternalChannelId,
  )
  const leadId = parsePositiveInt(raw.leadId)
  const conversationId = parsePositiveInt(raw.conversationId)

  if (screen === 'conversation' && leadId == null) {
    screen = activeProvider != null ? 'chats-list' : 'integrations-picker'
  }

  if (
    (screen === 'chats-list' ||
      screen === 'wazzup-accounts' ||
      screen === 'wazzup-channels') &&
    activeProvider == null
  ) {
    screen = 'integrations-picker'
  }

  if (
    (screen === 'wazzup-accounts' || screen === 'wazzup-channels') &&
    activeProvider != null &&
    activeProvider !== 'wazzup'
  ) {
    screen = 'chats-list'
  }

  if (screen === 'wazzup-accounts') {
    screen = 'integrations-picker'
  }

  if (screen === 'wazzup-channels' && chatsListIntegrationId == null) {
    screen = 'integrations-picker'
  }

  if (
    screen === 'chats-list' &&
    activeProvider === 'wazzup' &&
    (chatsListIntegrationId == null || chatsListExternalChannelId == null)
  ) {
    screen =
      chatsListIntegrationId != null ? 'wazzup-channels' : 'integrations-picker'
    chatsListExternalChannelId = null
  }

  if (activeProvider !== 'wazzup') {
    chatsListExternalChannelId = null
  }

  return {
    expanded,
    screen,
    activeProvider,
    chatsListIntegrationId,
    chatsListExternalChannelId,
    leadId,
    conversationId:
      screen === 'conversation' ? conversationId : null,
  }
}

const parsePersistedPayload = (
  parsed: unknown,
): IntegrationChatDockPersistedState | null => {
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return null
  }

  const record = parsed as Record<string, unknown>

  return sanitizePersistedState({
    expanded:
      typeof record.expanded === 'boolean' ? record.expanded : undefined,
    screen:
      record.screen != null
        ? parseIntegrationChatDockScreen(record.screen)
        : undefined,
    activeProvider: parseProvider(record.activeProvider),
    chatsListIntegrationId: parsePositiveInt(record.chatsListIntegrationId),
    chatsListExternalChannelId: parseExternalChannelId(
      record.chatsListExternalChannelId,
    ),
    leadId: parsePositiveInt(record.leadId),
    conversationId: parsePositiveInt(record.conversationId),
  })
}

const readLegacyPersistedState = (): IntegrationChatDockPersistedState | null => {
  if (typeof localStorage === 'undefined') {
    return null
  }

  try {
    const raw = localStorage.getItem(LEGACY_INTEGRATION_CHAT_DOCK_STORAGE_KEY)
    if (!raw) {
      return null
    }

    const parsed = JSON.parse(raw) as {
      leadId?: unknown
      expanded?: unknown
      screen?: unknown
    }

    return sanitizePersistedState({
      expanded:
        typeof parsed?.expanded === 'boolean' ? parsed.expanded : undefined,
      screen:
        parsed?.screen != null
          ? parseIntegrationChatDockScreen(parsed.screen)
          : undefined,
      leadId: parsePositiveInt(parsed?.leadId),
      activeProvider: null,
      chatsListIntegrationId: null,
      chatsListExternalChannelId: null,
      conversationId: null,
    })
  } catch {
    return null
  }
}

export const readIntegrationChatDockPersistedState =
  (): IntegrationChatDockPersistedState | null => {
    if (typeof localStorage === 'undefined') {
      return null
    }

    try {
      const raw = localStorage.getItem(INTEGRATION_CHAT_DOCK_NAVIGATION_STORAGE_KEY)
      if (raw) {
        const parsed = parsePersistedPayload(JSON.parse(raw))
        if (parsed) {
          return parsed
        }
      }

      return readLegacyPersistedState()
    } catch {
      return readLegacyPersistedState()
    }
  }

export const writeIntegrationChatDockPersistedState = (
  state: IntegrationChatDockPersistedState | null,
): void => {
  if (typeof localStorage === 'undefined') {
    return
  }

  if (state == null) {
    localStorage.removeItem(INTEGRATION_CHAT_DOCK_NAVIGATION_STORAGE_KEY)
    localStorage.removeItem(LEGACY_INTEGRATION_CHAT_DOCK_STORAGE_KEY)
    return
  }

  const payload = sanitizePersistedState(state)

  localStorage.setItem(
    INTEGRATION_CHAT_DOCK_NAVIGATION_STORAGE_KEY,
    JSON.stringify(payload),
  )
}

export const getInitialIntegrationChatDockSession =
  (): IntegrationChatDockInitialSession => {
    const persisted = readIntegrationChatDockPersistedState()
    if (!persisted) {
      return { ...DEFAULT_PERSISTED_STATE }
    }

    return persisted
  }

/** @deprecated use getInitialIntegrationChatDockSession */
export const getInitialIntegrationChatDockUiState =
  getInitialIntegrationChatDockSession
